import './PageSetupWizard.scss';

// Step Components (add additional here)
import { DenominatorLabels } from '@constants/page';
import { useAurigaContext } from '@context/AurigaContext';
import { usePageSetupContext } from '@context/PageSetupContext';
import { useTOCContext } from '@context/TOCContext';
import AsyncSaveButton from '@deckManagement/components/Common/AsyncSaveButton/AsyncSaveButton';
import LockDeckButton from '@deckManagement/components/Common/LockDeckButton/LockDeckButton';
import PageDenominatorStep from '@deckManagement/components/PageSetupWizard/components/PageDenominatorStep';
import PageDetails from '@deckManagement/components/PageSetupWizard/components/PageDetails';
import PageDriverStep from '@deckManagement/components/PageSetupWizard/components/PageDriverStep';
import TableConfiguration from '@deckManagement/components/PageSetupWizard/components/TableConfiguration';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { readableDenominatorSaveErrors } from '@utils/errorUtils';
import { isEmpty, isNil, map } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageSetupSteps } from 'types/PageSetupContext.types';

const PageSetupWizard = () => {
  const {
    pageSetupSteps,
    currPageName,
    currDeckId,
    currentStep,
    nextStep,
    setCurrentStep,
    tocId,
    tocEntry,
    pageName,
    tocName,
    isPageCreated,
    setIsPageCreated,
    selectedLayoutFamily,
    assignLayoutFamily,
    hasTableConfigChanged,
    snapshotTableConfig,
    resetTableConfig,
    setIsUpdateTemplate,
    savePageDrivers,
    pageDriversSaving,
    pageLockStatus,
    isPageLockStatusResolved,
    denominatorLabel,
    decklineNameFor,
  } = usePageSetupContext();
  const { updateTocEntry, saveChanges, deletePage } = useTOCContext();
  const { toastNotification } = useAurigaContext();
  const navigate = useNavigate();
  useRecordLoadComplete(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // Ref used to determine the need for Page Details saving on creation or updates
  const pendingPageDetailsSaveRef = useRef(false);
  // Ref used to trigger a deferred discard (delete) save
  const pendingDiscardRef = useRef(false);

  // To track the New Page workflow (versus Edit Page mode) in render
  const isNewPageWorkflow = isNil(currPageName) || isEmpty(currPageName);
  // To track whether we are on the last Step, and need to render the Save Page button
  const isLastStep = useMemo(
    () => (!isNil(currentStep) ? pageSetupSteps.indexOf(currentStep) === pageSetupSteps.length - 1 : false),
    [currentStep, pageSetupSteps],
  );
  /** A step's title: the % denominator step is titled with the page's label (e.g. "% Gross Margin"). */
  const stepTitle = useCallback(
    (step: PageSetupSteps) =>
      step === PageSetupSteps.PAGE_DENOMINATOR && denominatorLabel ? DenominatorLabels[denominatorLabel] : step,
    [denominatorLabel],
  );
  // Numeric conversion of Step string identifier from enum for use in Stepper
  const currStepIndex = useMemo(
    () => (!isNil(currentStep) ? pageSetupSteps.indexOf(currentStep) : 0),
    [currentStep, pageSetupSteps],
  );

  /**
   * Controls the DISCARD action.
   * - New Page flow: deletes the TOC entry (whether persisted or not) and navigates back to the Page Library.
   * - Edit Page flow: navigates back to the Page view. Any changes saved during step progression are retained.
   */
  const handleDiscard = useCallback(() => {
    if (isNewPageWorkflow) {
      if (!tocId) return;
      deletePage(tocId);
      if (isPageCreated) {
        setIsDiscarding(true);
        pendingDiscardRef.current = true;
      } else {
        navigate(`/deck-preparation/decks/${currDeckId}/toc`);
      }
    } else if (!isEmpty(selectedLayoutFamily)) {
      // Only navigate to page view if a layout template has been set up; otherwise, return to Page Library
      navigate(`/deck-preparation/decks/${currDeckId}/pages/${encodeURIComponent(pageName)}`);
    } else {
      navigate(`/deck-preparation/decks/${currDeckId}/toc`);
    }
  }, [isNewPageWorkflow, tocId, deletePage, isPageCreated, navigate, currDeckId, pageName, selectedLayoutFamily]);

  /**
   * Cleanup when the deck lock is released while in the wizard. Releasing the lock disables
   * editing, so the redirect guard routes back to the TOC on its own (no navigation here).
   *
   * For a new-page flow where the page has NOT been persisted yet (no "Create Page" save), the
   * auto-created TOC entry is still an unsaved row; deletePage drops it locally so it does not
   * linger as a phantom "pending create" row in the TOC. If the page was already created
   * (isPageCreated), it is a valid saved page and is intentionally left intact — releasing the
   * lock is not the same as discarding a created page.
   */
  const handleDeckReleased = useCallback(() => {
    if (isNewPageWorkflow && tocId && !isPageCreated) {
      deletePage(tocId);
    }
  }, [isNewPageWorkflow, tocId, isPageCreated, deletePage]);

  /**
   * Controls the NEXT step progression click.
   * Validates current step before advancing to the next step.
   * Results in updates to Current and Next steps in the PageSetupContext
   */
  const handleNextStep = useCallback(() => {
    if (isNil(nextStep)) return;

    // Validate Table Configuration step before advancing
    if (currentStep === PageSetupSteps.TABLE_CONFIGURATION) {
      // A reusable layout family must be selected before advancing. Grain coverage is
      // intentionally NOT enforced here (or in TableConfiguration): templates are not
      // authored 1:1 per page grain (e.g. Summary), so any catalog family is selectable and
      // getPageLayout resolves each grain at render time. This gate only requires that a
      // family was chosen.
      if (isEmpty(selectedLayoutFamily)) {
        toastNotification({
          message: 'Please select a Layout Template before proceeding.',
          visible: true,
          type: 'warning',
        });
        return;
      }

      // Validation passed — skip save if nothing changed from the persisted selection
      if (!hasTableConfigChanged) {
        setCurrentStep(nextStep);
        return;
      }

      setIsSaving(true);
      assignLayoutFamily(
        () => {
          setIsSaving(false);
          setIsUpdateTemplate(true);
          snapshotTableConfig();
          // Update step incomplete status to the next step
          if (tocId) updateTocEntry(tocId, 'group_name', `SETUP_INCOMPLETE|${nextStep}`);
          toastNotification({
            message: 'Layout template assigned successfully.',
            visible: true,
            type: 'success',
          });
          setCurrentStep(nextStep);
        },
        (error) => {
          setIsSaving(false);
          toastNotification({
            message: `Unable to assign layout template: ${error.message}`,
            visible: true,
            type: 'error',
          });
        },
      );
      return;
    }

    // Save Page Details edits when returning to the Page Details step after the initial TOC item and page(s) creation
    if (currentStep === PageSetupSteps.PAGE_DETAILS && isPageCreated && tocEntry) {
      // Internal ID is used for updates as it will differ from toc_id here
      const { internalId } = tocEntry.metadata;
      updateTocEntry(internalId, 'page_name', pageName);
      updateTocEntry(internalId, 'toc_page_name', tocName);
      updateTocEntry(internalId, 'group_name', `SETUP_INCOMPLETE|${nextStep}`);
      setIsSaving(true);
      // Trigger next save of Page Details
      pendingPageDetailsSaveRef.current = true;
      return;
    }

    // Default action to just increment next step
    setCurrentStep(nextStep);
  }, [
    nextStep,
    currentStep,
    isPageCreated,
    tocEntry,
    setCurrentStep,
    toastNotification,
    selectedLayoutFamily,
    assignLayoutFamily,
    hasTableConfigChanged,
    snapshotTableConfig,
    setIsUpdateTemplate,
    tocId,
    updateTocEntry,
    pageName,
    tocName,
  ]);

  /**
   * Controls the PREVIOUS step progression click.
   * Results in updates to Current and Next steps in the PageSetupContext
   */
  const handlePrevStep = useCallback(() => {
    if (!isNil(currentStep) && currStepIndex > 0) {
      setCurrentStep(pageSetupSteps[currStepIndex - 1]);
    }
  }, [currStepIndex, currentStep, pageSetupSteps, setCurrentStep]);

  /**
   * Handles the Save Page action on the Page Driver step.
   * Persists page driver selections via savePageDrivers, as well as page denominator_label,
   * and displays a toast notification on success or failure.
   */
  const handleSavePage = useCallback(() => {
    // NOTE: Setting prior to success callback to prevent delay/flicker in disabled state of Button.
    setIsRedirecting(true);

    savePageDrivers(
      () => {
        toastNotification({
          message: 'Page drivers saved successfully.',
          visible: true,
          type: 'success',
        });
        // Redirect to Page
        // TODO: Determine if this is needed when modifying existing page.
        navigate(`/deck-preparation/decks/${currDeckId}/pages/${encodeURIComponent(pageName)}`);
      },
      (error) => {
        setIsRedirecting(false);
        const readable = readableDenominatorSaveErrors(error, decklineNameFor);
        (readable.length > 0 ? readable : [`Unable to save page: ${error.message}`]).forEach((message) =>
          toastNotification({ message, visible: true, type: 'error' }),
        );
      },
    );
  }, [currDeckId, decklineNameFor, navigate, pageName, savePageDrivers, toastNotification]);

  /**
   * Handles the Create Page action on the Page Details step.
   * Pushes user edits back to TOCContext, persists via SaveDeck, and advances to Table Configuration.
   */
  const handleCreatePage = useCallback(() => {
    if (!tocId || !nextStep) return;

    // Pushes the User edits back to the TOC entry for saving (and to allow capturing of Audit History)
    // Note: On first CREATE tocId is aligned with metadata.internalId for new TOC items (see createNewTOCEntry comment)
    updateTocEntry(tocId, 'page_name', pageName);
    updateTocEntry(tocId, 'toc_page_name', tocName);
    // Update step incomplete status to the next step
    updateTocEntry(tocId, 'group_name', `SETUP_INCOMPLETE|${nextStep}`);

    setIsSaving(true);
    // Trigger a need for Page Details Save
    pendingPageDetailsSaveRef.current = true;
  }, [tocId, nextStep, updateTocEntry, pageName, tocName]);

  // To track whether we are on the Create Page step (New Page flow, Page Details → Table Configuration)
  const isCreatePageStep =
    !isLastStep && isNewPageWorkflow && !isPageCreated && nextStep === PageSetupSteps.TABLE_CONFIGURATION;

  // Redirect to Page Library if the current user does not hold THIS page's lock (editing disabled)
  useEffect(() => {
    // Wait until the deck id is known and lock status has actually resolved; otherwise a
    // fresh reload would redirect with an unresolved (null) deck id and/or a pre-load
    // default "editing disabled" state.
    if (currDeckId && isPageLockStatusResolved && pageLockStatus.isEditingDisabled) {
      navigate(`/deck-preparation/decks/${currDeckId}/toc`, { replace: true });
    }
  }, [pageLockStatus.isEditingDisabled, isPageLockStatusResolved, currDeckId, navigate]);

  // Deferred Save: UseEffect will run after re-render so that saveChanges has access to the latest
  // modifiedTocEntries when pendingPageDetailsSaveRef is true
  useEffect(() => {
    if (!pendingPageDetailsSaveRef.current) return;
    pendingPageDetailsSaveRef.current = false;
    saveChanges(
      () => {
        setIsSaving(false);
        setIsPageCreated(true);
        toastNotification({
          message: 'Page(s) for reporting periods have been created successfully.',
          visible: true,
          type: 'success',
        });
        setCurrentStep(nextStep!);
      },
      () => {
        setIsSaving(false);
      },
    );
  });

  // Deferred Discard: UseEffect will run after re-render so that saveChanges has access to the
  // deletion-marked modifiedTocEntries when pendingDiscardRef is true
  useEffect(() => {
    if (!pendingDiscardRef.current) return;
    pendingDiscardRef.current = false;
    saveChanges(
      () => navigate(`/deck-preparation/decks/${currDeckId}/toc`),
      () => {
        setIsDiscarding(false);
        toastNotification({
          message: 'Failed to discard changes. Please try again.',
          visible: true,
          type: 'error',
        });
      },
    );
  });

  return (
    <Box className='page-setup-wizard'>
      <Box className='header'>
        <Box className='header-title-section'>
          <Typography variant='h5'>{isNewPageWorkflow ? 'Page Setup - New' : 'Page Setup - Edit'}</Typography>
        </Box>
        <Box className='button-group'>
          <AsyncSaveButton
            inProgress={isDiscarding}
            inProgressContent='Discarding...'
            buttonProps={{
              className: 'step-discard-button',
              variant: 'text',
              size: 'medium',
              disabled: isDiscarding || isSaving,
              onClick: handleDiscard,
            }}
          >
            {isNewPageWorkflow ? 'Discard' : 'Close'}
          </AsyncSaveButton>
          {currStepIndex !== 0 && (
            <Button
              className='step-previous-button'
              variant='outlined'
              size='medium'
              disabled={isSaving || (currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged)}
              onClick={handlePrevStep}
            >
              Previous Step
            </Button>
          )}
          {isCreatePageStep && (
            <AsyncSaveButton
              inProgress={isSaving}
              inProgressContent='Creating Page...'
              buttonProps={{
                className: 'step-next-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: isSaving,
                onClick: handleCreatePage,
              }}
            >
              Create Page
            </AsyncSaveButton>
          )}
          {currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged && (
            <Button variant='outlined' size='medium' disabled={isSaving} onClick={resetTableConfig}>
              Discard
            </Button>
          )}
          {!isLastStep && !isCreatePageStep && (
            <AsyncSaveButton
              inProgress={isSaving}
              inProgressContent='Saving...'
              buttonProps={{
                className: 'step-next-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: isSaving,
                onClick: handleNextStep,
              }}
            >
              {currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged
                ? 'Save Config'
                : 'Next Step'}
            </AsyncSaveButton>
          )}
          {isLastStep && (
            <AsyncSaveButton
              inProgress={pageDriversSaving || isRedirecting}
              inProgressContent='Saving...'
              buttonProps={{
                className: 'save-page-button',
                variant: 'contained',
                color: 'primary',
                size: 'medium',
                disabled: pageDriversSaving || isRedirecting,
                onClick: handleSavePage,
              }}
            >
              Save Page
            </AsyncSaveButton>
          )}
          {/* Lock/Release Deck Button — reachable here only while the deck is locked by the
              current user, so it renders as "Release Deck". Mounting it also raises the app-wide
              deck-lock banner on this route (the alert is raised by LockDeckButton's effect).
              Releasing the deck flips editing to disabled, which the redirect guard handles by
              routing back to the TOC. */}
          <LockDeckButton onReleaseSuccess={handleDeckReleased} />
        </Box>
      </Box>
      {!isNil(currentStep) && (
        <Box className='page-setup-stepper'>
          {/* Left Stepper — clickable in edit mode to allow direct step navigation */}
          <Stepper activeStep={currStepIndex} orientation='vertical' nonLinear={!isNewPageWorkflow}>
            {map(pageSetupSteps, (step, idx) => (
              <Step key={`${step}-${idx}`} completed={!isNewPageWorkflow && idx !== currStepIndex}>
                {!isNewPageWorkflow ? (
                  <StepButton
                    className='wizard-selectable-step'
                    disabled={isSaving || (currentStep === PageSetupSteps.TABLE_CONFIGURATION && hasTableConfigChanged)}
                    onClick={() => setCurrentStep(step)}
                  >
                    {stepTitle(step)}
                  </StepButton>
                ) : (
                  <StepLabel>{stepTitle(step)}</StepLabel>
                )}
              </Step>
            ))}
          </Stepper>

          {/* Right Step Content - TODO: Will render individual step components */}
          <Box className='step-content'>
            {currentStep === PageSetupSteps.PAGE_DETAILS && <PageDetails />}
            {currentStep === PageSetupSteps.TABLE_CONFIGURATION && <TableConfiguration />}
            {currentStep === PageSetupSteps.PAGE_DRIVER && <PageDriverStep />}
            {currentStep === PageSetupSteps.PAGE_DENOMINATOR && <PageDenominatorStep />}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PageSetupWizard;
